/*global google */

export interface MapRouterMarker {
  lat: number;
  lng: number;
}

export class MapRouter {
  private readonly map: google.maps.Map;
  private readonly directionsService: google.maps.DirectionsService;
  private readonly directionsRenderer: google.maps.DirectionsRenderer;
  private readonly duration?: (duration: number) => void;
  private readonly distance?: (distance: number) => void;

  constructor({
    map,
    durationCalculated,
    distanceCalculated,
    strokeColor = '#0088FF',
    strokeOpacity = 0.6,
    strokeWeight = 6,
  }: {
    map: google.maps.Map;
    durationCalculated?: (duration: number) => void;
    distanceCalculated?: (distance: number) => void;
    strokeColor?: string;
    strokeOpacity?: number;
    strokeWeight?: number;
  }) {
    this.map = map;
    this.directionsService = new google.maps.DirectionsService();

    this.directionsRenderer = new google.maps.DirectionsRenderer({
      map: this.map,
      suppressMarkers: true,
      polylineOptions: {
        clickable: false,
        strokeColor: strokeColor,
        strokeOpacity: strokeOpacity,
        strokeWeight: strokeWeight,
      },
      preserveViewport: true,
    });
    this.duration = durationCalculated;
    this.distance = distanceCalculated;
  }

  private splitMarkers(markers: MapRouterMarker[]) {
    const segmentSize = 25;
    const segments = [];
    let currentSegment = [];
    for (let i = 0; i < markers.length; i++) {
      currentSegment.push(markers[i]);
      if (i === markers.length - 1) {
        // Last element was pushed on, save the current segment as it will never reach max size for the segement
        segments.push(currentSegment);
      } else if (currentSegment.length === segmentSize - 1) {
        // Max size of segment reached. Add current plus the next entry (first of the next segment) to segment, and start a new one.
        currentSegment.push(markers[i + 1]);
        segments.push(currentSegment);
        currentSegment = [];
      }
    }
    return segments;
  }

  private createRequest(
    markers: MapRouterMarker[]
  ): google.maps.DirectionsRequest {
    const originMarker = markers[0];
    const destinationMarker = markers[markers.length - 1];
    const wayPointMarkers = markers.slice(1, markers.length - 1);
    const directionsRequest: google.maps.DirectionsRequest = {
      origin: new google.maps.LatLng(originMarker.lat, originMarker.lng),
      destination: new google.maps.LatLng(
        destinationMarker.lat,
        destinationMarker.lng
      ),
      waypoints: wayPointMarkers.map(wayPointMarker => {
        return {
          location: new google.maps.LatLng(
            wayPointMarker.lat,
            wayPointMarker.lng
          ),
        };
      }),
      travelMode: google.maps.TravelMode.DRIVING,
    };
    return directionsRequest;
  }

  public hide() {
    this.directionsRenderer.setMap(null);
  }

  public show() {
    this.directionsRenderer.setMap(this.map);
  }

  public drawRoute = (markers: MapRouterMarker[]) => {
    const requests = this.splitMarkers(markers).map(markerSegment =>
      this.createRequest(markerSegment)
    );

    const { directionsRenderer, mergeDirectionsResults, distance, duration } =
      this;

    const collectedRoutingResults: google.maps.DirectionsResult[] = [];
    let missingRoutes = requests.length;
    requests.forEach((request, index) => {
      this.directionsService.route(request, function (currentResult, status) {
        missingRoutes--;
        if (status === google.maps.DirectionsStatus.OK) {
          collectedRoutingResults.push(currentResult);
        }
        if (missingRoutes === 0) {
          const mergedDirections = mergeDirectionsResults(
            collectedRoutingResults
          );

          directionsRenderer.setDirections(mergedDirections);

          const legs = mergedDirections.routes[0]?.legs || [];

          if (duration) {
            const durationInSecs = legs.map(leg => leg.duration.value);
            const totalDuration = durationInSecs.reduce(
              (acc, cur) => acc + cur,
              0
            );
            duration(Math.ceil(totalDuration / 60));
          }

          if (distance) {
            const distanceInMeters = legs.map(leg => leg.distance.value);
            const totalDistance = distanceInMeters.reduce(
              (acc, cur) => acc + cur,
              0
            );
            distance(totalDistance);
          }
        }
      });
    });
  };

  private mergeDirectionsResults(
    collectedRoutingResults: google.maps.DirectionsResult[]
  ): google.maps.DirectionsResult {
    if (collectedRoutingResults.length === 0) {
      return { geocoded_waypoints: [], routes: [] };
    }
    const baseDirectionsResult = collectedRoutingResults[0];
    const concatenatedResult = collectedRoutingResults.reduce(
      (resultingResult, current, index) => {
        resultingResult.geocoded_waypoints =
          resultingResult.geocoded_waypoints.concat(current.geocoded_waypoints);

        if (resultingResult.routes.length === 0) {
          resultingResult.routes.push(current.routes[0]);
        } else {
          resultingResult.routes[0].legs =
            resultingResult.routes[0].legs.concat(current.routes[0].legs);
          resultingResult.routes[0].overview_path =
            resultingResult.routes[0].overview_path.concat(
              current.routes[0].overview_path
            );
          resultingResult.routes[0].bounds =
            resultingResult.routes[0].bounds.extend(
              current.routes[0].bounds.getNorthEast()
            );
          resultingResult.routes[0].bounds =
            resultingResult.routes[0].bounds.extend(
              current.routes[0].bounds.getSouthWest()
            );
        }

        return resultingResult;
      },
      { geocoded_waypoints: [], routes: [] }
    );

    baseDirectionsResult.geocoded_waypoints =
      concatenatedResult.geocoded_waypoints;
    baseDirectionsResult.routes = concatenatedResult.routes;

    return baseDirectionsResult;
  }
}
